import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import DOMPurify from 'dompurify'
import { useAuth } from '../../context/AuthContext'
import * as clubsApi from '../../api/clubs'
import type { ClubDetail } from '../../types'
import { EventsTimeline } from '../../components/clubs/EventsTimeline'
import { MemberLocationMap } from '../../components/maps/MemberLocationMap'

export function ClubPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuth()
  const [detail, setDetail] = useState<ClubDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // The viewer's AUID, not the user object: a primitive, so this effect reruns
  // when the viewer is identified rather than on every render that hands back a
  // new object.
  const viewerAuid = user?.auid

  // Refetch when the viewer becomes known.
  //
  // GET /clubs/:slug is optionalAuth. A signed-in club officer gets their own
  // details back in the officer list even when they are doNotPublish; an
  // anonymous caller does not. On a direct load or a refresh of /clubs/:slug
  // this effect used to run once, with [slug], and it ran BEFORE the bearer
  // token existed: AuthProvider only has one after refreshToken() and then
  // getMe(), two network round trips started in its own mount effect. So the
  // request went out unauthenticated, the server correctly returned the
  // filtered list, and nothing ever refetched. Four doNotPublish secretaries
  // lost their edit link on a direct load, though they kept it when navigating
  // in-app, where auth had already resolved.
  //
  // Deliberately not gated on isLoading. This page is public, and making every
  // anonymous visitor wait for two auth round trips before any content appears
  // would be a worse trade than one extra request for a signed-in viewer.
  useEffect(() => {
    if (!slug) return
    let active = true

    async function load() {
      try {
        const data = await clubsApi.getClub(slug!)
        // The unauthenticated response can land after the authenticated one.
        // Without this guard the slower request wins and the carve-out is lost
        // again, intermittently, which is worse than losing it every time.
        if (active) setDetail(data)
      } catch {
        if (active) setError('Club not found or failed to load.')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()

    return () => { active = false }
  }, [slug, viewerAuid])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
        <span className="ml-3 text-text-muted">Loading club...</span>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Club Not Found</h1>
        <p className="text-text-muted mb-4">{error || 'This club could not be found.'}</p>
        <Link to="/clubs" className="text-primary hover:underline">Browse all clubs</Link>
      </div>
    )
  }

  const { club, page, officers, memberCount } = detail
  const events = detail.events ?? []
  const memberMap = detail.memberMap ?? { points: [], totalMapped: 0, totalMembers: memberCount }
  const secretary = officers.find((o) => o.role.includes('Secretary'))
  const description = page?.description || `${club.clubName} is an American Racing Pigeon Union club in ${club.clubState || 'the United States'}.`

  // Check if current user is a secretary of this club (for edit link)
  const isSecretary = user && officers.some(
    (o) => o.role.includes('Secretary') && o.auid === user.auid
  )

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsClub',
    name: club.clubName,
    description,
    address: {
      '@type': 'PostalAddress',
      addressRegion: club.clubState,
      addressCountry: 'US',
    },
    ...(page?.contactEmail && { email: page.contactEmail }),
    ...(page?.contactPhone && { telephone: page.contactPhone }),
    ...(page?.website && { url: page.website }),
    memberOf: {
      '@type': 'SportsOrganization',
      name: 'American Racing Pigeon Union',
      url: 'https://pigeon.org',
    },
  }

  return (
    <>
      <Helmet>
        <title>{club.clubName} - ARPU Club</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${club.clubName} - ARPU Club`} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        {page?.photoUrl && <meta property="og:image" content={page.photoUrl} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <Link to="/clubs" className="text-sm text-primary hover:underline mb-2 inline-block">
              &larr; All Clubs
            </Link>
            <h1 className="text-2xl font-heading font-bold text-text">{club.clubName}</h1>
            <p className="text-text-light">
              {club.clubState && <span>{club.clubState} &middot; </span>}
              Zone {club.clubZone}
              <span className="ml-3 text-text-muted">
                {memberCount} member{memberCount !== 1 ? 's' : ''}
              </span>
            </p>
          </div>
          {isSecretary && (
            <Link
              to={`/clubs/${slug}/edit`}
              className="px-4 py-2 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors text-sm"
            >
              Edit Page
            </Link>
          )}
        </div>

        {/* Photo */}
        {page?.photoUrl && (
          <div className="mb-6">
            <img
              src={page.photoUrl}
              alt={club.clubName}
              className="w-full max-h-80 object-cover rounded-lg"
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Custom content */}
            {page?.customContent && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <div
                  className="prose prose-sm max-w-none text-text"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.customContent) }}
                />
              </div>
            )}

            {/* Description */}
            {page?.description && !page.customContent && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <p className="text-text">{page.description}</p>
              </div>
            )}

            {/* Events & Announcements (combined calendar) */}
            <EventsTimeline events={events} />

            {/* Officers */}
            {officers.length > 0 && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <h2 className="font-heading font-semibold text-text mb-4">Officers</h2>
                <div className="space-y-3">
                  {officers.map((officer) => (
                    <div key={officer.auid} className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-text">{officer.first} {officer.last}</p>
                        <p className="text-sm text-text-muted">{officer.role}</p>
                      </div>
                      <div className="text-right text-sm">
                        {officer.phone && (
                          <a href={`tel:${officer.phone}`} className="block text-text-muted hover:text-primary">
                            {officer.phone}
                          </a>
                        )}
                        {officer.email && (
                          <a href={`mailto:${officer.email}`} className="block text-text-muted hover:text-primary">
                            {officer.email}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact info */}
            {(page?.contactEmail || page?.contactPhone || page?.website || page?.meetingInfo) && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <h2 className="font-heading font-semibold text-text mb-3">Club Info</h2>

                {page.meetingInfo && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-text-muted uppercase mb-1">Meeting Info</p>
                    <p className="text-sm text-text">{page.meetingInfo}</p>
                  </div>
                )}

                {page.contactPhone && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-text-muted uppercase mb-1">Phone</p>
                    <a href={`tel:${page.contactPhone}`} className="text-sm text-primary hover:underline">
                      {page.contactPhone}
                    </a>
                  </div>
                )}

                {page.contactEmail && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-text-muted uppercase mb-1">Email</p>
                    <a href={`mailto:${page.contactEmail}`} className="text-sm text-primary hover:underline">
                      {page.contactEmail}
                    </a>
                  </div>
                )}

                {page.website && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-text-muted uppercase mb-1">Website</p>
                    <a href={page.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">
                      {page.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Social media */}
            {page?.socialMedia && Object.keys(page.socialMedia).length > 0 && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <h2 className="font-heading font-semibold text-text mb-3">Social Media</h2>
                <div className="space-y-2">
                  {Object.entries(page.socialMedia).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-primary hover:underline capitalize"
                    >
                      {platform}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Race results on the National Database */}
            <div className="border border-border rounded-lg p-6 bg-surface">
              <h2 className="font-heading font-semibold text-text mb-3">Race Results</h2>
              <p className="text-sm text-text-muted mb-3">
                Race reports and national award results are published on the ARPU National Database.
              </p>
              <div className="space-y-2">
                <a
                  href="https://pigeon-ndb.com/races/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-primary hover:underline"
                >
                  Race Reports &amp; Results &rarr;
                </a>
                <a
                  href="https://pigeon-ndb.com/results/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-primary hover:underline"
                >
                  National Award Results &rarr;
                </a>
              </div>
            </div>

            {/* Quick contact if no page but has secretary */}
            {!page && secretary && (
              <div className="border border-border rounded-lg p-6 bg-surface">
                <h2 className="font-heading font-semibold text-text mb-3">Contact</h2>
                <p className="text-sm text-text mb-1">Secretary: {secretary.first} {secretary.last}</p>
                {secretary.phone && (
                  <a href={`tel:${secretary.phone}`} className="block text-sm text-primary hover:underline">
                    {secretary.phone}
                  </a>
                )}
                {secretary.email && (
                  <a href={`mailto:${secretary.email}`} className="block text-sm text-primary hover:underline">
                    {secretary.email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Member locations map (full width) */}
        <div className="mt-6">
          <MemberLocationMap data={memberMap} />
        </div>

        {page?.lastUpdated && (
          <p className="text-xs text-text-muted mt-8">
            Last updated: {new Date(page.lastUpdated).toLocaleDateString()}
          </p>
        )}
      </div>
    </>
  )
}
